import { IBaseItem, ITicketContext as ICapturedContext } from 'O3_core';

/**
 * The codes behind the `support.ticket_status` catalog rows.
 *
 * Kept as an enum even though the values now live in the database, because the
 * few places that must *reason* about a state — "can this reporter still sign
 * off?" — need a constant to compare against, and a row id is per-tenant. Text
 * and colour always come from the row; only branching uses these.
 */
export enum TicketStatusCode {
  New = 'new',
  Triaged = 'triaged',
  InProgress = 'in_progress',
  Escalated = 'escalated',
  Resolved = 'resolved',
  /**
   * Support proposed a solution and handed the ticket to the reporter for
   * sign-off. Not terminal — the reporter accepts or rejects next.
   */
  Closed = 'closed',
  /** The reporter accepted the solution. The only terminal state. */
  Accepted = 'accepted',
}

export enum TicketSeverityCode {
  Critical = 'critical',
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

export enum TicketCategoryCode {
  Bug = 'bug',
  ChangeRequest = 'change_request',
  Question = 'question',
  DataIssue = 'data_issue',
}

/** Shared shape of the three support catalogs. */
export interface ITicketCatalogItem extends IBaseItem {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  sortOrder?: number;
}

/**
 * A catalog row that also carries its own badge palette. The colours are data,
 * so a status can be recoloured without touching the stylesheet.
 */
export interface ITicketStatus extends ITicketCatalogItem {
  bgColor?: string | null;
  fgColor?: string | null;
  icon?: string | null;
  isInitial?: boolean;
  isTerminal?: boolean;
}

export type ITicketSeverity = ITicketStatus;

export type ITicketCategory = ITicketCatalogItem;

export enum CommentAuthorKind {
  User = 'user',
  Support = 'support',
  Dev = 'dev',
}

/** Detector name → match count. Never the matched values. */
export type IPhiScrubHits = Record<string, number>;

/**
 * What the server returns in `ticket.context`.
 *
 * Derived from the snapshot type `ClientDiagnosticsService` produces rather
 * than restated: the two had already drifted on which fields were optional,
 * and only one of them can be right. Every field is optional here because the
 * column is nullable and older tickets predate fields added later — the
 * producer's own type keeps `correlationIds`/`failedRequests` required, which
 * is correct for something it always fills in.
 */
export type ITicketContext = Partial<ICapturedContext>;

export type { IFailedRequestRecord, IClientErrorRecord } from 'O3_core';

/**
 * The list projection returned by both filter endpoints. `createdAt` and the
 * other timestamps arrive as ISO strings on the wire, so they are typed as
 * strings rather than `Date` — `IBaseEntity` would force `Date` here and every
 * consumer would then be lying about what it holds.
 */
export interface ITicket extends IBaseItem {
  id: string;
  publicRef: string;
  categoryId: string;
  category?: ITicketCategory | null;
  statusId: string;
  status?: ITicketStatus | null;
  title: string;
  severityId?: string | null;
  severity?: ITicketSeverity | null;
  typeId?: string | null;
  type?: ITicketCategory | null;
  createdAt: string;
  resolvedAt?: string | null;
  closedAt?: string | null;
  assignedToUserId?: string | null;
  reporterUserId: string;
}

export interface ITicketComment {
  id: string;
  ticketId: string;
  body: string;
  authorKind: CommentAuthorKind;
  isInternal: boolean;
  /**
   * The proposed solution awaiting the reporter's sign-off. A flag, not a
   * separate record — cleared on rejection, leaving the comment in the thread.
   */
  isSolution: boolean;
  authorUserId: string;
  /** Snapshotted at write time; null on comments predating the column. */
  authorName?: string | null;
  createdAt: string;
}

export interface ITicketAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  ticketId: string;
  /** Set when the file hangs off a specific comment rather than the ticket. */
  ticketCommentId?: string;
  createdAt: string;
}

export interface ITicketAttachmentDownload extends ITicketAttachment {
  presignedUrl: string;
  expiresInSeconds: number;
}

/** Reporter detail — no `phiScrubHits`, internal comments already stripped. */
export interface ITicketDetail extends ITicket {
  description: string;
  context?: ITicketContext | null;
  suspectedArea?: string | null;
  duplicateOfTicketId?: string | null;
  escalatedAt?: string | null;
  resolvedByUserId?: string | null;
  closedByUserId?: string | null;
  comments: ITicketComment[];
  attachments: ITicketAttachment[];
}

/** Server-captured exception detail. No request or response payloads by design. */
export interface IServerErrorDiagnostic {
  correlationId: string;
  at: string;
  durationMs?: number;
  method: string;
  routeTemplate: string;
  status: number;
  exception: { name: string; message: string; stack: string[] };
  tenantId?: string;
  userId?: string;
  roles?: string[];
}

export interface ITicketDiagnostics {
  serverErrors: IServerErrorDiagnostic[];
  /** Client-reported failures the server has no record of. */
  unmatchedCorrelationIds: string[];
  resolvedAt: string;
}

/**
 * Console detail — adds the scrub telemetry, the internal thread, and the
 * server-side diagnostics. Absent from `ITicketDetail` on purpose: stack
 * traces are for support and dev, never for the reporter.
 */
export interface ITicketConsoleDetail extends ITicketDetail {
  phiScrubHits?: IPhiScrubHits | null;
  diagnostics?: ITicketDiagnostics | null;
}

export interface ICreateTicketRequest {
  title: string;
  description: string;
  /** A `ticket_category` row id, from `TicketCategoryService`. */
  categoryId: string;
  context?: ITicketContext;
}

/**
 * A partial update: an absent key leaves the stored value alone, and an
 * explicit `null` clears it. Both are needed — omitting everything falsy would
 * make a field impossible to blank. The server's `@IsOptional()` lets `null`
 * through validation and its handler writes it.
 */
export interface ITriageTicketRequest {
  typeId?: string | null;
  severityId?: string | null;
  suspectedArea?: string | null;
  assignedToUserId?: string | null;
  duplicateOfTicketId?: string | null;
}

export interface ICreateTicketCommentRequest {
  ticketId: string;
  body: string;
  isInternal?: boolean;
  /**
   * Who is speaking, decided by the screen the comment is written on rather
   * than by the caller's roles. The console sends `support`, "My tickets"
   * sends `user`. Required, matching the server DTO — the reporter route then
   * forces `user` regardless of what arrives.
   */
  authorKind: CommentAuthorKind;
}

/** Closing is proposing a solution — the text is required. */
export interface ICloseTicketRequest {
  solution: string;
}

/** Rejecting sends the ticket back — the reason is required. */
export interface IRejectTicketRequest {
  reason: string;
}
